import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, inject, isDevMode } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { EventBusEvents } from '@siza-staff-care/event-bus-constants';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import { NoState } from '@siza-staff-care/shared/data';
import { SnackbarService } from '@siza-staff-care/shared/ui-components/common-ui';
import { GlobalStoreRegistryStore } from '@siza-staff-care/shared/ui-components/store-registry';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { MetaData, NgEventBus } from 'ng-event-bus';
import { Observable, tap } from 'rxjs';

const AllowInDevMode = false;

@Component({
	selector: 'sh-shell',
	template: `
		@if (appState.vm$ | async; as vm) {
			<div class="wrapper" [class.security-design-mode]="vm.isPaintingSecurity">
				<router-outlet id="app-outlet" />

				<!-- Container for the slide-out overlay used for clocking in/out from login. -->
				<div id="slide-out-overlay"></div>

				<div class="paint-mode-overlay"></div>

				<div class="paint-mode-border"></div>
			</div>
		}
	`,
	styles: [
		`
			:host {
				display: block;
				height: 100vh;

				.wrapper {
					height: 100%;

					.paint-mode-overlay,
					.paint-mode-border {
						display: none;
					}

					&.security-design-mode {
						.paint-mode-overlay {
							display: block;
							height: 100vh;
							width: 100vw;
							top: 0;
							left: 0;
							position: absolute;
							z-index: 1000;
							pointer-events: none;
						}

						.paint-mode-border {
							display: block;
							height: 100vh;
							width: 100vw;
							top: 0;
							left: 0;
							position: absolute;
							z-index: 1001;
							pointer-events: none;
							border: 5px dashed red;
							background-color: transparent;
						}
					}
				}
			}
		`,
	],
	standalone: true,
	imports: [CommonModule, RouterModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent extends ComponentStore<NoState> {
	protected appState = inject(AppStateService);
	private hotkeys = inject(HotkeysService);
	private eventBus = inject(NgEventBus);
	private snackbar = inject(SnackbarService);

	private hotkey?: Hotkey[];

	constructor(private storeRegistry: GlobalStoreRegistryStore) {
		super({});

		this.hotkey = this.hotkeys.add([
			new Hotkey(
				'ctrl+alt+m',
				() => {
					this.eventBus.cast(EventBusEvents.Event_Activate_Menu);
					return false;
				},
				['INPUT', 'SELECT', 'TEXTAREA'],
				'Activate menu',
			),
			new Hotkey(
				'ctrl+alt+t',
				() => {
					this.eventBus.cast(EventBusEvents.Event_Toggle_Menu);
					return false;
				},
				['INPUT', 'SELECT', 'TEXTAREA'],
				'Toggle side menu',
			),
		]) as Hotkey[];

		this.listenForGenericSnackbarMessage(this.eventBus.on(EventBusEvents.Event_ShowSnackbar_Message));
	}

	private readonly listenForGenericSnackbarMessage = this.effect((event$: Observable<MetaData>) =>
		event$.pipe(
			tap((event) => {
				const msg = event.data as string;
				this.snackbar.showMessage(msg);
			}),
		),
	);

	override ngOnDestroy(): void {
		super.ngOnDestroy();

		if (!this.hotkey) {
			return;
		}

		this.hotkeys.remove(this.hotkey);
	}

	@HostListener('window:beforeunload', ['$event'])
	unloadHandler(e: BeforeUnloadEvent) {
		// Don't prompt for reload if in development mode (as it would bring up a dialog each time hot reload triggers)
		if (!AllowInDevMode && isDevMode()) {
			return;
		}

		let shouldWarn = this.appState.isAuthenticated && this.storeRegistry.hasUnsavedChangesOrDialogOrModalShowing();
		if (this.appState.customisation?.suppressWindowUnloadDialog) {
			shouldWarn = false;
		}

		if (shouldWarn) {
			e.preventDefault();
			// Required for Chrome to work properly. Without setting the return value, the dialog is never shown to the user.
			e.returnValue = false;
		}
	}
}
